import { z } from 'zod';

// ---------------------------------------------------------------------------
// Organization security policy: two-step verification, session lifetime and login IP ranges.
// ---------------------------------------------------------------------------

/** Every field is optional so each setting can be saved on its own. Null = use the server default. */
export const securityPolicySchema = z
  .object({
    requireMfa: z.boolean().optional(),
    /** Absolute session lifetime: 1 hour to 30 days. */
    sessionHours: z.number().int().min(1).max(720).nullable().optional(),
    /** Signed out after this long without activity: 15 minutes to 24 hours. */
    idleMinutes: z.number().int().min(15).max(1440).nullable().optional(),
    /** How long the audit log is kept: 90 days to 10 years. */
    auditRetentionDays: z.number().int().min(90).max(3650).optional(),
    /** How long field history is kept: 6 months to 10 years. */
    historyRetentionMonths: z.number().int().min(6).max(120).optional(),
  })
  .strict();
export type SecurityPolicyInput = z.infer<typeof securityPolicySchema>;

export interface SecurityPolicy {
  requireMfa: boolean;
  sessionHours: number | null;
  idleMinutes: number | null;
  auditRetentionDays: number;
  historyRetentionMonths: number;
  /** The server-wide default lifetime used when sessionHours is null. */
  defaultSessionHours: number;
  members: number;
  enrolled: number;
  ipRanges: IpRange[];
  /** The address this request came from, as the server sees it. */
  currentIp: string;
}

export interface IpRange { id: string; cidr: string; description: string | null; createdAt: string }

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

/** An IPv4 or IPv6 address, optionally with a prefix length (203.0.113.0/24, 2001:db8::/32). */
export function isCidr(s: string): boolean {
  const [addr, prefix, extra] = s.split('/');
  if (extra !== undefined || !addr) return false;
  const v4 = IPV4.test(addr);
  const v6 = !v4 && addr.includes(':') && /^[0-9a-fA-F:.]+$/.test(addr) && addr.split('::').length <= 2;
  if (!v4 && !v6) return false;
  if (prefix === undefined) return true;
  if (!/^\d{1,3}$/.test(prefix)) return false;
  return Number(prefix) <= (v4 ? 32 : 128);
}

export const ipRangeSchema = z
  .object({
    cidr: z.string().trim().min(2).max(64).refine(isCidr, 'Enter an IP address or range, like 203.0.113.0/24'),
    description: z.string().trim().max(120).nullable().optional(),
  })
  .strict();
export const IP_RANGES_PER_ORG = 50;
